import * as d3 from "d3";
import { bindTickTooltip } from "./utils/tickTooltip";
import {
  planCategoryAxisLabels,
  planRotatedCategoryAxisLabels,
  truncateToLimit,
  X_AXIS_NAME_RESERVE_PX,
} from "./utils/categoryAxisLabels";

const getYearTicks = (scale: d3.ScaleTime<number, number>): Date[] => {
  return scale.ticks(d3.timeYear.every(1)!);
};

/** Fallback when a caller passes dimensions without a bottom margin. */
const DEFAULT_MARGIN_BOTTOM = 60;

export function drawAxes(
  plotClassName: string,

  scales: any,
  {
    /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
    width,
    height,
    marginTop,
    marginLeft,
    marginBottom = DEFAULT_MARGIN_BOTTOM,
    padding,
    /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
    bottomPadding,
  }: {
    width: number;
    height: number;
    marginTop: number;
    marginLeft: number;
    marginBottom?: number;
    padding: number;
    bottomPadding: number;
  },
  options: { xAxisSvgClassName?: string; verticalXAxisLabels?: boolean } = {
    xAxisSvgClassName: "",
  },
) {
  const svg = d3.select(`svg.${plotClassName}`);

  const hasXAxisSvg = options.xAxisSvgClassName;
  const leftScale = scales?.find((scale: any) => scale?.position === "left");
  const bottomScale = scales?.find(
    (scale: any) => scale?.position === "bottom",
  );
  const topScale = scales?.find((scale: any) => scale?.position === "top");
  // Chosen by measurement while planning the x-axis below, so it has to be
  // readable from the rotation block at the end of this function.
  let rotateXAxisLabels = Boolean(options.verticalXAxisLabels);
  if (leftScale) {
    const subGroupScale = scales?.find(
      (_scale: any) => _scale?.type === "subgroup",
    );
    const hasSubGroupScale = subGroupScale && leftScale.type === "categorical";

    const mainLabelLimit = hasSubGroupScale ? 18 : 24;
    const subgroupLabelLimit = 10;

    const yTimeTicks =
      leftScale.type === "time" ? getYearTicks(leftScale.scale) : undefined;

    svg
      .append("g")
      .classed("y-axis", true)

      .attr("transform", `translate(${marginLeft + padding},0)`)
      .call(
        d3
          .axisLeft(leftScale.scale as d3.AxisScale<d3.AxisDomain>)
          .tickSize(0)
          .tickPadding(hasSubGroupScale ? 80 : 5)
          .ticks(5)
          .tickValues(
            leftScale.type === "time" && yTimeTicks
              ? yTimeTicks
              : (null as any),
          )
          .tickFormat((d) => {
            if (leftScale.type === "time") {
              return String(new Date(d as number).getFullYear());
            }
            const hasDictionary =
              leftScale.dictionary &&
              Object.keys(leftScale.dictionary).length > 0;
            const dictValue = hasDictionary
              ? leftScale.dictionary[d.toString()]
              : undefined;
            const fallbackStr = leftScale.showPercentage
              ? `${d}%`
              : d.toString();
            const str = (dictValue ?? fallbackStr) as string;

            return str.length > mainLabelLimit
              ? `${str.slice(0, mainLabelLimit)}...`
              : str;
          }),
      )
      .attr("font-family", "Work Sans")
      .attr("fill", "#556D75")
      .call((g) => g.select(".domain").attr("stroke", "#D5DADC"))
      .selectAll(".tick text")

      .call((s) =>
        bindTickTooltip(
          s as unknown as d3.Selection<SVGTextElement, unknown, any, any>,
          plotClassName,
        ),
      );

    if (hasSubGroupScale && subGroupScale?.scale) {
      leftScale.scale.domain().forEach((d: d3.AxisDomain) => {
        svg
          .append("g")
          .attr(
            "transform",
            `translate(${marginLeft + padding},${leftScale.scale(d)})`,
          )
          .classed("subgroup-axis y-axis", true)
          .call(
            d3
              .axisLeft(subGroupScale.scale)
              .tickPadding(5)
              .tickSize(0)
              .tickFormat((d) => {
                const str = d.toString();
                if (str === "PLACEBO") return "PBO";
                return str.length > subgroupLabelLimit
                  ? `${str.slice(0, subgroupLabelLimit)}...`
                  : str;
              }),
          )
          .attr("text-anchor", "end")
          .attr("font-family", "Work Sans")
          .attr("fill", "#556D75")
          .selectAll(".tick text")
          .call((s) =>
            bindTickTooltip(
              s as unknown as d3.Selection<SVGTextElement, unknown, any, any>,
              plotClassName,
            ),
          );
      });
    }
  }
  if (bottomScale || topScale) {
    const scaleInUse = bottomScale || topScale;
    const thisScale = scaleInUse.scale;

    const isCategorical = scaleInUse.type === "categorical";
    const isTime = scaleInUse.type === "time";
    const timeTicks = isTime ? getYearTicks(thisScale) : undefined;
    const subGroupScale = scales?.find(
      (_scale: any) => _scale?.type === "subgroup",
    );
    const hasSubGroupScale = subGroupScale && isCategorical;

    const axisFunction =
      scaleInUse.position === "top" ? d3.axisTop : d3.axisBottom;

    const xAxisSvg = hasXAxisSvg
      ? d3.select(`svg.${options.xAxisSvgClassName}`)
      : svg;

    // Labels are centred on their band, so the distance that decides whether
    // neighbours collide is centre-to-centre (`step`), not the drawn bar width.
    const slotWidthPx =
      typeof thisScale.step === "function" ? thisScale.step() : null;
    const categoryCount = isCategorical ? thisScale.domain().length : 0;

    const horizontalPlan = planCategoryAxisLabels({
      categoryCount,
      slotWidthPx,
    });
    // Deliberately still keyed on config, not on measurement.
    //
    // Rotating whenever `horizontalPlan.tickInterval > 1` looks strictly
    // better and is not: the bottom margin is sized in getPlotDimensions.ts,
    // which gates `rotatedLabelMarginBottom` on this same config flag. Rotate
    // without that and the labels get the base 60px margin — a four-to-five
    // character budget — so WEEK 4/8/12/16 all render "WEEK…". That is the
    // ambiguity this file exists to remove, turned on its side.
    //
    // Horizontal thinning is NOT config-gated, so an unconfigured axis still
    // improves: fewer labels, each inside its slot and each unambiguous.
    // Auto-rotation can return once sizing makes the same decision from the
    // same measurement, which needs the label domain threaded through
    // createPlotData -> plotConfig -> getPlotDimensions.
    rotateXAxisLabels = Boolean(options.verticalXAxisLabels);

    const labelPlan = rotateXAxisLabels
      ? planRotatedCategoryAxisLabels({
          categoryCount,
          slotWidthPx,
          availableHeightPx: marginBottom - X_AXIS_NAME_RESERVE_PX,
        })
      : horizontalPlan;

    // Too narrow to identify anything: show fewer labels rather than
    // overprinting every one of them. The bands themselves are untouched.
    const thinnedCategoryTicks =
      isCategorical && labelPlan.tickInterval > 1
        ? thisScale
            .domain()
            .filter((_: unknown, i: number) => i % labelPlan.tickInterval === 0)
        : undefined;

    xAxisSvg
      .append("g")
      .classed("x-axis", true)
      .attr(
        "transform",
        `translate(0, ${
          scaleInUse.position === "top"
            ? marginTop
            : hasXAxisSvg
              ? 0
              : height + marginTop
        })`,
      )
      .call(
        (axisFunction as any)(thisScale)
          .tickPadding(hasSubGroupScale ? 20 : 5)
          .tickSize(0)
          .tickFormat((d: d3.AxisDomain) => {
            if (scaleInUse.showPercentage) {
              return `${d}%`;
            }
            if (scaleInUse.type === "time") {
              const data = new Date(d as number | string | Date);
              return String(data.getFullYear());
            }
            const str = d !== null && d !== undefined ? d.toString() : "-";
            return truncateToLimit(str, labelPlan.charLimit);
          })

          .ticks(4)
          .tickValues(
            scaleInUse.numericScale
              ? thisScale.domain().filter(function (
                  d: d3.AxisDomain,
                  i: number,
                ) {
                  const maxValue = Math.max(...thisScale.domain());
                  const interval = Math.ceil(maxValue / 10) * 2; // Round up to nearest 10 and double
                  return !(i % interval) || i === 0;
                })
              : scaleInUse.type === "total"
                ? thisScale
                    .ticks(4)
                    .filter((d: d3.AxisDomain) => Number.isInteger(d))
                : scaleInUse.type === "time"
                  ? timeTicks
                  : thinnedCategoryTicks,
          ),
      )

      .attr("font-family", "Work Sans")

      .attr("text-anchor", "middle")
      .attr("fill", "#556D75")
      .call((g) => g.select(".domain").attr("stroke", "#D5DADC"))
      .selectAll(".tick text")
      .call((s) =>
        bindTickTooltip(
          s as unknown as d3.Selection<SVGTextElement, unknown, any, any>,
          plotClassName,
        ),
      );
    if (hasSubGroupScale && subGroupScale?.scale) {
      /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
      thisScale.domain().forEach((d: d3.AxisDomain, index: number) => {
        svg
          .append("g")
          .attr(
            "transform",
            `translate(${thisScale(d)},${
              scaleInUse.position === "top" ? marginTop : height + marginTop
            })`,
          )
          .classed("subgroup-axis x-axis", true)
          .call(
            (axisFunction as any)(subGroupScale.scale)
              .tickPadding(5)
              .tickSize(0)
              .tickFormat((d: d3.AxisDomain) => {
                // Transform specific text values
                if (d === "PLACEBO") return "PBO";
                const str = d.toString();
                if (str.length > 6) return str.slice(0, 6) + ".";
                return str;
              })
              .tickValues(
                subGroupScale?.scale?.domain().filter(function (
                  d: d3.AxisDomain,
                  i: number,
                ) {
                  const maxValue = Math.max(...thisScale.domain());
                  const interval = Math.ceil(maxValue / 10) * 2;
                  return !(i % interval);
                }),
              ),
          )
          .attr("text-anchor", "middle")
          .attr("font-family", "Work Sans")
          .attr("fill", "#556D75");
      });
    }

    if (hasXAxisSvg) {
      d3.selectAll(`svg.${options.xAxisSvgClassName} .tick text`)
        .style("font-size", "12px")
        .attr("text-anchor", "middle");
    }

    svg
      .selectAll(".tick text")
      .style("font-size", "12px")
      .attr("text-anchor", "middle");

    svg.selectAll(".subgroup-axis path").remove();
  }

  svg.selectAll(".subgroup-axis.y-axis path").remove();

  // When adding x-axis labels. The axis may live in a separate svg when the
  // caller passes `xAxisSvgClassName`; selecting only from the main svg would
  // silently find nothing and skip the rotation entirely.
  const xAxis = hasXAxisSvg
    ? d3.selectAll(
        `svg.${plotClassName} .x-axis, svg.${options.xAxisSvgClassName} .x-axis`,
      )
    : svg.selectAll(".x-axis");
  if (rotateXAxisLabels) {
    xAxis
      .selectAll("text")
      .style("text-anchor", "end")
      .attr("dx", "-.8em")
      .attr("dy", "-.3em")
      .attr("transform", "rotate(-90)");
  }
}
